import { List } from 'immutable'

import { convertProviderTreatmentModalityToTreatmentOption } from '../common/constants/providerTreatmentModalityConstants'

/**
 * The read-only slice of an Immutable `Map` this module needs.
 *
 * Deliberately structural rather than `Map<string, $TSFixMe>`: naming `Map` here makes `tsc`
 * compare Immutable's own recursive `Comparator`/`Iterable` generics at every call site, which
 * overflows into `TS2321`/`TS2589` (`useBookAppointment.ts` hands us a `Map` built from `fromJS`).
 */
interface ImmutableAppointment {
  get(key: string, notSetValue?: $TSFixMe): $TSFixMe
  getIn(keyPath: string[], notSetValue?: $TSFixMe): $TSFixMe
}

export interface CreateAppointmentPayloadParams {
  providerID?: string | null
  /** Falls back to the appointment's own `provider.modalities` when omitted. */
  providerModalities?: $TSFixMe
  userID?: string
  appointment: ImmutableAppointment
  referenceAppointment: ImmutableAppointment
}

/**
 * Builds the appointment-creation request body, reading each field from `appointment` and
 * falling back to `referenceAppointment` (usually the client's most recent appointment).
 */
export const createAppointmentPayload = ({
  providerID = null,
  providerModalities = null,
  userID,
  appointment,
  referenceAppointment,
}: CreateAppointmentPayloadParams) => {
  const treatmentType = appointment.get('treatmentType', referenceAppointment.get('treatmentType'))
  const modalities = providerModalities ?? appointment.getIn(['provider', 'modalities'], List())
  const selectedProviderModality = modalities.find(
    (modality: $TSFixMe) => convertProviderTreatmentModalityToTreatmentOption(modality) === treatmentType,
  )

  return {
    address: appointment.get('address', referenceAppointment.get('address', '')),
    appointmentDuration: appointment.get('appointmentDuration', 50),
    appointmentId: appointment.get('appointmentId'),
    appointmentStatus: appointment.get('appointmentStatus', referenceAppointment.get('appointmentStatus')),
    appointmentType: appointment.get('appointmentType', referenceAppointment.get('appointmentType', 'googleCalendar')),
    details: appointment.get('details', referenceAppointment.get('details') || ''),
    eventId: appointment.get('eventId'),
    lyraProviderId: providerID || appointment.get('lyraProviderId', referenceAppointment.get('lyraProviderId')),
    meetingFormat: appointment.get('meetingFormat', 'video'),
    phone: appointment.get('phone', referenceAppointment.get('phone')),
    phoneType: appointment.get('phoneType', referenceAppointment.get('phoneType')),
    selectedProviderModality,
    ...(appointment.get('sessionType') && { sessionType: appointment.get('sessionType') }),
    startDate: appointment.get('startDate'),
    startTime: appointment.get('startTime'),
    supportedVisitTypes: appointment.get('supportedVisitTypes', referenceAppointment.get('supportedVisitTypes') || 1),
    textAppDownloadLink: false,
    title: appointment.get('title', 'New Appointment'),
    timeZone: appointment.get('timeZone'),
    treatmentType: appointment.get('treatmentType', referenceAppointment.get('treatmentType')),
    userId: userID || appointment.get('userId', referenceAppointment.get('userId')),
  }
}
